import { defineMessages, type IntlShape } from 'react-intl';

import { SEARCH_COUNT_SINGLE_VALUE_ID } from '~/components/Shared/Charts/constants';
import { PilotId as EdsPilotId } from '~/eds/src/types/PilotId';
import { getUserName } from '~/eds/src/utils/getUserName';
import { SaveSearchPermissionType } from '~/enums/SaveSearchPermissionType';
import { UserRoleType } from '~/enums/UserRoleType';
import { FilterViewType } from '~/evifields/src/types/FilterView';
import { SearchQuery } from '~/features/advanced-search/types';
import { getIsSidebarNavAvailable } from '~/features/app/useIsSidebarNavAvailable';
import { FlagType } from '~/flags/flags';
import { getFlag } from '~/flags/useFlag';
import { intlService } from '~/i18n/IntlService';
import {
  getResource,
  getResources,
  JsonApiCreateResponse,
} from '~/redux/api/ApiTypes';
import { pilotV3 } from '~/services/serviceInstances';
import { ColumnSortOrder } from '~/types/Columns';
import { ChartType } from '~/types/DashboardV2';
import { EntityQuery } from '~/types/OpenSearch';
import { PilotId } from '~/types/PilotId';
import { User } from '~/types/User';
import { DEFAULT_TABLE_SORT_COLUMN_ORDER } from '~/utils/table';

import { toSortParam } from '../utils';

export type DashboardEntityType =
  | 'ticket'
  | 'workflow'
  | 'document'
  | 'ticket_v2';
export type DashboardListAttribute = {
  created_date: string;
  creator_id: number;
  creator_name: string;
  last_modified_by_id: number | null;
  last_modified_by_name: string | null;
  entity_type: DashboardEntityType;
  modified_date: string | null;
  name: string;
  is_default: boolean;
  visibility: SaveSearchPermissionType;
};

export type DashboardTypesQueryParam = 'mine' | 'shared';

export interface DashboardChart {
  filter_id: number | string;
  sorted_index: number;
  interval?: string;
  width?: string;
  type?: ChartType;
}

export interface DashboardTableColumn {
  filter_id: number | string;
  column_index: number;
}

export interface Dashboard {
  charts: DashboardChart[];
  columns: string[];
  filters: Array<number | string>;
  filtersView?: Record<EdsPilotId, FilterViewType>;
  id: string;
  name: string;
  query: EntityQuery[];
  is_default: boolean;
  userIds?: PilotId[];
  creatorId: PilotId;
  departmentIds?: PilotId[];
  visibility?: SaveSearchPermissionType;
  /**
   * TODO: make 'entity' mandatory once we have the backend support.
   * Currently, this is only available in pre sig dashboards mocks.
   */
  entity?: DashboardEntityType;
}

export interface DashboardAndPathname extends Dashboard {
  pathname: string;
}

export type DashboardAttributes = {
  entity_type?: DashboardEntityType;
  charts: DashboardChart[];
  filters: Array<number | string>;
  name: string;
  query: EntityQuery[];
  result_table: DashboardTableColumn[];
  is_default: boolean;
  user_ids?: PilotId[];
  department_ids?: PilotId[];
  visibility?: SaveSearchPermissionType;
  creator_id: PilotId;
};

export type DashboardEntityData = {
  attributes: DashboardAttributes;
  id?: string;
  type: string;
};

type DashboardEntity = {
  uuid: string;
  data: DashboardEntityData;
};

type DashboardEntityPartial = {
  uuid: string;
  attributes: Partial<DashboardAttributes>;
};

export interface SavedDashboardsParams {
  page: number;
  pageSize: number;
  return_defaults?: boolean;
  sortBy?: ColumnSortOrder;
  types?: DashboardTypesQueryParam[];
}

export interface SavedDashboard extends DashboardListAttribute {
  id: string;
}

export interface SavedDashboardsMeta {
  page: {
    count: number;
    current: number;
  };
  total: number;
}

export const createDefaultDashboards = (): Promise<[]> =>
  pilotV3.post('/dashboards/defaults/');

const preSigDashboardNames = defineMessages({
  open_tickets: {
    id: 'DashboardV2.openTicketsDashboardName',
    defaultMessage: 'Open Tickets',
    description: 'Default dashboard name for the open tickets dashboard',
  },
  completed_tickets: {
    id: 'DashboardV2.completedTicketsDashboardName',
    defaultMessage: 'Completed Tickets',
    description: 'Default dashboard name for the completed tickets dashboard',
  },
  open_tickets_v2: {
    id: 'DashboardV2.openTicketsV2DashboardName',
    defaultMessage: 'Open Tickets',
    description: 'Default dashboard name for the K2 open tickets dashboard',
  },
  completed_tickets_v2: {
    id: 'DashboardV2.completedTicketsV2DashboardName',
    defaultMessage: 'Completed Tickets',
    description:
      'Default dashboard name for the K2 completed tickets dashboard',
  },
});

// The "Requests" rename only ships to customers opted into the new sidenav —
// the pre-sidenav side nav, top nav, and page title still say "Tickets".
const preSigDashboardNamesRenamed = defineMessages({
  open_tickets: {
    id: 'DashboardV2.openRequestsDashboardName',
    defaultMessage: 'Open Requests',
    description: 'Default dashboard name for the open tickets dashboard',
  },
  completed_tickets: {
    id: 'DashboardV2.completedRequestsDashboardName',
    defaultMessage: 'Completed Requests',
    description: 'Default dashboard name for the completed tickets dashboard',
  },
});

type PreSigDashboardUuid =
  | 'open_tickets'
  | 'completed_tickets'
  | 'open_tickets_v2'
  | 'completed_tickets_v2';

const RENAMEABLE_UUID: Record<
  PreSigDashboardUuid,
  'open_tickets' | 'completed_tickets'
> = {
  open_tickets: 'open_tickets',
  completed_tickets: 'completed_tickets',
  open_tickets_v2: 'open_tickets',
  completed_tickets_v2: 'completed_tickets',
};

const getPreSigDashboardName = (uuid: PreSigDashboardUuid, intl: IntlShape) =>
  intl.formatMessage(
    getIsSidebarNavAvailable() && getFlag(FlagType.CLMEvolutionPhase1)
      ? preSigDashboardNamesRenamed[RENAMEABLE_UUID[uuid]]
      : preSigDashboardNames[uuid],
  );

export const fetchDashboardV2: getResource<
  'dashboard',
  DashboardAttributes
> = ({ uuid }) => {
  // [DashboardMocks] - TODO remove mock once we create the pre sig dashboards in pilot.
  if (
    uuid === 'open_tickets' ||
    uuid === 'completed_tickets' ||
    uuid === 'open_tickets_v2' ||
    uuid === 'completed_tickets_v2'
  ) {
    const mock =
      uuid === 'open_tickets_v2' || uuid === 'completed_tickets_v2'
        ? preSigDashboardMocksV2[uuid]
        : preSigDashboardMocks[uuid];
    return {
      ...mock,
      data: {
        ...mock.data,
        attributes: {
          ...mock.data.attributes,
          name: getPreSigDashboardName(uuid, intlService.getIntl()),
        },
      },
    } as any;
  }
  return pilotV3.get(`/dashboards/dashboard/${uuid}/`);
};

export const generateDashboardToSave = (
  dashboard: Dashboard,
  dashboardId: string,
  query: SearchQuery,
) => {
  return {
    attributes: {
      charts: dashboard
        ? dashboard.charts?.map((d, i) => {
            return {
              filter_id: d.filter_id,
              sorted_index: i,
              interval: d.interval,
              width: d.width,
              type: d.type,
            } as DashboardChart;
          })
        : [],
      result_table: dashboard
        ? dashboard.columns?.map((f, i) => {
            return {
              filter_id: f,
              column_index: i,
            } as DashboardTableColumn;
          })
        : [],
      filters: dashboard?.filters || [],
      name: dashboard?.name || '',
      query: query || [],
      is_default: !!dashboard?.is_default,
      entity_type: dashboard?.entity,
      creator_id: dashboard.creatorId,
    },
    id: dashboardId,
    type: 'dashboard',
  };
};

export const saveDashboardChanges = async ({
  uuid,
  data,
}: DashboardEntity): Promise<any> =>
  await pilotV3.put(`/dashboards/dashboard/${uuid}/`, { data });

export const updateDashboard = async ({
  uuid,
  attributes,
}: DashboardEntityPartial): Promise<
  JsonApiCreateResponse<'dashboard', DashboardEntityPartial>
> =>
  await pilotV3.patch(`dashboards/dashboard/${uuid}/`, {
    data: { attributes, id: uuid, type: 'dashboard' },
  });

export const saveAsDashboard = async (
  data: DashboardEntityData,
): Promise<JsonApiCreateResponse<'save-as-dashboard', DashboardEntityData>> =>
  await pilotV3.post('/dashboards/dashboard/', { data });

export const fetchSavedDashboards: getResources<
  'dashboard_metadata',
  DashboardListAttribute,
  SavedDashboardsParams,
  SavedDashboardsMeta
> = ({
  page,
  pageSize,
  return_defaults = false,
  sortBy = DEFAULT_TABLE_SORT_COLUMN_ORDER,
  types,
}) => {
  return pilotV3.get('/dashboards/', {
    params: {
      'page[number]': page,
      'page[size]': pageSize,
      return_defaults,
      sort: toSortParam(sortBy),
      types,
    },
  });
};

export const deleteDashboard = ({ uuid }: { uuid: string }): Promise<void> =>
  pilotV3.remove(`dashboards/dashboard/${uuid}/`);

const preSigDashboardMocks = {
  open_tickets: {
    meta: {
      is_complex_query: false,
    },
    data: {
      id: 'open_tickets',
      type: 'dashboard',
      attributes: {
        name: 'Open Tickets',
        entity_type: 'ticket',
        charts: [
          {
            filter_id: SEARCH_COUNT_SINGLE_VALUE_ID,
            sorted_index: 0,
            width: '3',
          },
          {
            filter_id: 'ticket_age_Ticket',
            sorted_index: 1,
          },
          {
            filter_id: 'internal_turns_age_Ticket',
            sorted_index: 2,
          },
          {
            filter_id: 'external_turns_age_Ticket',
            sorted_index: 3,
          },
          {
            filter_id: 'current_party_type_Ticket',
            sorted_index: 4,
          },
          {
            filter_id: 'workflow_name_Ticket',
            sorted_index: 5,
          },
          {
            filter_id: 'submitter_department_Ticket',
            sorted_index: 6,
          },
          {
            filter_id: 'assignee_name_Ticket',
            sorted_index: 7,
          },
          {
            filter_id: 'stage_Ticket',
            sorted_index: 8,
          },
          {
            filter_id: 'ticket_last_activity_date_Ticket',
            sorted_index: 9,
          },
        ],
        filters: [
          'name_Ticket',
          'ticket_submit_date_Ticket',
          'submitter_Ticket',
          'submitter_department_Ticket',
          'ticket_last_activity_date_Ticket',
          'stage_Ticket',
        ],
        query: [
          {
            type: 'filter',
            id: 'ticket_completed_date_Ticket',
            operator: 'is_blank',
            value: {
              value_list: [],
              value_meta: {},
            },
          },
          {
            type: 'operator',
            value: 'and',
          },
          {
            type: 'filter',
            id: 'ticket_cancelled_date_Ticket',
            operator: 'is_blank',
            value: {
              value_list: [],
              value_meta: {},
            },
          },
        ],
        result_table: [
          {
            filter_id: 'name_Ticket',
            column_index: 0,
          },
          {
            filter_id: 'ticket_age_Ticket',
            column_index: 1,
          },
          {
            filter_id: 'internal_turns_age_Ticket',
            column_index: 2,
          },
          {
            filter_id: 'external_turns_age_Ticket',
            column_index: 3,
          },
          {
            filter_id: 'current_party_type_Ticket',
            column_index: 4,
          },
          {
            filter_id: 'ticket_submit_date_Ticket',
            column_index: 5,
          },
          {
            filter_id: 'submitter_Ticket',
            column_index: 6,
          },
          {
            filter_id: 'submitter_department_Ticket',
            column_index: 7,
          },
          {
            filter_id: 'ticket_last_activity_date_Ticket',
            column_index: 8,
          },
          {
            filter_id: 'stage_Ticket',
            column_index: 9,
          },
        ],
        is_default: true,
        visibility: 'PRIVATE',
        user_ids: [],
        department_ids: [],
      },
    },
  },
  completed_tickets: {
    meta: {
      is_complex_query: false,
    },
    data: {
      id: 'completed_tickets',
      type: 'dashboard',
      attributes: {
        entity_type: 'ticket',
        name: 'Completed Tickets',
        charts: [
          {
            filter_id: SEARCH_COUNT_SINGLE_VALUE_ID,
            sorted_index: 0,
            width: '3',
          },
          {
            filter_id: 'ticket_age_Ticket',
            sorted_index: 1,
          },
          {
            filter_id: 'internal_turns_age_Ticket',
            sorted_index: 2,
          },
          {
            filter_id: 'external_turns_age_Ticket',
            sorted_index: 3,
          },
          {
            filter_id: 'ticket_completed_date_Ticket',
            sorted_index: 4,
            type: 'line',
          },
          {
            filter_id: 'workflow_name_Ticket',
            sorted_index: 5,
          },
          {
            filter_id: 'submitter_department_Ticket',
            sorted_index: 6,
          },
        ],
        filters: [
          'ticket_submit_date_Ticket',
          'ticket_last_activity_date_Ticket',
          'workflow_name_Ticket',
          'submitter_Ticket',
          'submitter_department_Ticket',
        ],
        query: [
          {
            type: 'filter',
            id: 'ticket_completed_date_Ticket',
            operator: 'is_not_blank',
            value: {
              value_list: [],
              value_meta: {},
            },
          },
        ],
        result_table: [
          {
            filter_id: 'name_Ticket',
            column_index: 0,
          },
          {
            filter_id: 'ticket_age_Ticket',
            column_index: 1,
          },
          {
            filter_id: 'internal_turns_age_Ticket',
            column_index: 2,
          },
          {
            filter_id: 'external_turns_age_Ticket',
            column_index: 3,
          },
          {
            filter_id: 'ticket_submit_date_Ticket',
            column_index: 4,
          },
          {
            filter_id: 'submitter_Ticket',
            column_index: 5,
          },
          {
            filter_id: 'submitter_department_Ticket',
            column_index: 6,
          },
          {
            filter_id: 'ticket_last_activity_date_Ticket',
            column_index: 7,
          },
          {
            filter_id: 'stage_Ticket',
            column_index: 8,
          },
          {
            filter_id: 'ticket_completed_date_Ticket',
            column_index: 9,
          },
        ],
        is_default: true,
        visibility: 'PRIVATE',
        user_ids: [],
        department_ids: [],
      },
    },
  },
};

const preSigDashboardMocksV2 = {
  open_tickets_v2: {
    meta: {
      is_complex_query: false,
    },
    data: {
      id: 'open_tickets_v2',
      type: 'dashboard',
      attributes: {
        name: 'Open Tickets',
        entity_type: 'ticket_v2',
        charts: [
          {
            filter_id: SEARCH_COUNT_SINGLE_VALUE_ID,
            sorted_index: 0,
            width: '3',
          },
          {
            filter_id: 'ticket_age_TicketV2',
            sorted_index: 1,
          },
          {
            filter_id: 'workflow_id_TicketV2',
            sorted_index: 2,
            // workflow_id is registered MULTI_SELECT on the backend so users
            // can filter by several workflows at once, but each ticket only
            // ever belongs to one - force the donut Everest's equivalent
            // single-select workflow_name filter renders by default.
            type: 'donut',
          },
          {
            filter_id: 'submitter_TicketV2',
            sorted_index: 3,
          },
          {
            filter_id: 'submitter_department_TicketV2',
            sorted_index: 4,
          },
          {
            filter_id: 'current_stage_name_TicketV2',
            sorted_index: 5,
          },
          {
            filter_id: 'updated_at_TicketV2',
            sorted_index: 6,
          },
        ],
        filters: [
          'name_TicketV2',
          'created_at_TicketV2',
          'submitter_TicketV2',
          'submitter_department_TicketV2',
          'updated_at_TicketV2',
          'current_stage_name_TicketV2',
        ],
        query: [
          {
            type: 'filter',
            id: 'ticket_completed_date_TicketV2',
            operator: 'is_blank',
            value: {
              value_list: [],
              value_meta: {},
            },
          },
          {
            type: 'operator',
            value: 'and',
          },
          {
            type: 'filter',
            id: 'ticket_cancelled_date_TicketV2',
            operator: 'is_blank',
            value: {
              value_list: [],
              value_meta: {},
            },
          },
        ],
        result_table: [
          {
            filter_id: 'name_TicketV2',
            column_index: 0,
          },
          {
            filter_id: 'ticket_age_TicketV2',
            column_index: 1,
          },
          {
            filter_id: 'workflow_id_TicketV2',
            column_index: 2,
          },
          {
            filter_id: 'created_at_TicketV2',
            column_index: 3,
          },
          {
            filter_id: 'submitter_TicketV2',
            column_index: 4,
          },
          {
            filter_id: 'submitter_department_TicketV2',
            column_index: 5,
          },
          {
            filter_id: 'updated_at_TicketV2',
            column_index: 6,
          },
          {
            filter_id: 'current_stage_name_TicketV2',
            column_index: 7,
          },
        ],
        is_default: true,
        visibility: 'PRIVATE',
        user_ids: [],
        department_ids: [],
      },
    },
  },
  completed_tickets_v2: {
    meta: {
      is_complex_query: false,
    },
    data: {
      id: 'completed_tickets_v2',
      type: 'dashboard',
      attributes: {
        entity_type: 'ticket_v2',
        name: 'Completed Tickets',
        charts: [
          {
            filter_id: SEARCH_COUNT_SINGLE_VALUE_ID,
            sorted_index: 0,
            width: '3',
          },
          {
            filter_id: 'ticket_age_TicketV2',
            sorted_index: 1,
          },
          {
            filter_id: 'ticket_completed_date_TicketV2',
            sorted_index: 2,
            type: 'line',
          },
          {
            filter_id: 'workflow_id_TicketV2',
            sorted_index: 3,
            // See open_tickets_v2 above: workflow_id is MULTI_SELECT for
            // filtering, but each ticket has exactly one workflow, so force
            // the donut chart.
            type: 'donut',
          },
          {
            filter_id: 'submitter_TicketV2',
            sorted_index: 4,
          },
          {
            filter_id: 'submitter_department_TicketV2',
            sorted_index: 5,
          },
        ],
        filters: [
          'created_at_TicketV2',
          'updated_at_TicketV2',
          'workflow_id_TicketV2',
          'submitter_TicketV2',
          'submitter_department_TicketV2',
        ],
        query: [
          {
            type: 'filter',
            id: 'ticket_completed_date_TicketV2',
            operator: 'is_not_blank',
            value: {
              value_list: [],
              value_meta: {},
            },
          },
        ],
        result_table: [
          {
            filter_id: 'name_TicketV2',
            column_index: 0,
          },
          {
            filter_id: 'ticket_age_TicketV2',
            column_index: 1,
          },
          {
            filter_id: 'created_at_TicketV2',
            column_index: 2,
          },
          {
            filter_id: 'submitter_TicketV2',
            column_index: 3,
          },
          {
            filter_id: 'submitter_department_TicketV2',
            column_index: 4,
          },
          {
            filter_id: 'updated_at_TicketV2',
            column_index: 5,
          },
          {
            filter_id: 'current_stage_name_TicketV2',
            column_index: 6,
          },
          {
            filter_id: 'ticket_completed_date_TicketV2',
            column_index: 7,
          },
        ],
        is_default: true,
        visibility: 'PRIVATE',
        user_ids: [],
        department_ids: [],
      },
    },
  },
};

// [DashboardMocks] - TODO remove mock once we create the pre sig dashboards in pilot.
export const preSigMocksV2 = (currentUser: User, intl: IntlShape) => {
  if (
    currentUser.role === UserRoleType.PowerUserWithReadOnly ||
    currentUser.role === UserRoleType.Reviewer ||
    currentUser.role === UserRoleType.WorkflowOnly
  ) {
    return [];
  }
  return [
    {
      name: getPreSigDashboardName('open_tickets_v2', intl),
      creator_id: currentUser.id,
      creator_name: getUserName({
        user: currentUser,
        intl,
      }),
      created_date: '2026-09-03T00:00:00.000000-03:00',
      modified_date: '2026-09-03T00:00:00.000000-03:00',
      is_default: true,
      last_modified_by_id: null,
      last_modified_by_name: null,
      visibility: 'PRIVATE',
      id: 'open_tickets_v2',
      entity_type: 'ticket_v2',
    },
    {
      name: getPreSigDashboardName('completed_tickets_v2', intl),
      creator_id: currentUser.id,
      creator_name: getUserName({
        user: currentUser,
        intl,
      }),
      created_date: '2026-09-03T00:00:00.000000-03:00',
      modified_date: '2026-09-03T00:00:00.000000-03:00',
      is_default: true,
      last_modified_by_id: null,
      last_modified_by_name: null,
      visibility: 'PRIVATE',
      id: 'completed_tickets_v2',
      entity_type: 'ticket_v2',
    },
  ];
};

// [DashboardMocks] - TODO remove mock the pre sig dashboards in pilot is available.
export const preSigMocks = (currentUser: User, intl: IntlShape) => {
  if (
    currentUser.role === UserRoleType.PowerUserWithReadOnly ||
    currentUser.role === UserRoleType.Reviewer ||
    currentUser.role === UserRoleType.WorkflowOnly
  ) {
    return [];
  }
  return [
    {
      name: getPreSigDashboardName('open_tickets', intl),
      creator_id: currentUser.id,
      creator_name: getUserName({
        user: currentUser,
        intl,
      }),
      created_date: '2023-06-16T16:15:26.459184-03:00',
      modified_date: '2023-06-16T16:15:26.459206-03:00',
      is_default: true,
      last_modified_by_id: null,
      last_modified_by_name: null,
      visibility: 'PRIVATE',
      id: 'open_tickets',
      entity_type: 'ticket',
    },
    {
      name: getPreSigDashboardName('completed_tickets', intl),
      creator_id: currentUser.id,
      creator_name: getUserName({
        user: currentUser,
        intl,
      }),
      created_date: '2023-06-16T16:15:26.459184-03:00',
      modified_date: '2023-06-16T16:15:26.459206-03:00',
      is_default: true,
      last_modified_by_id: null,
      last_modified_by_name: null,
      visibility: 'PRIVATE',
      id: 'completed_tickets',
      entity_type: 'ticket',
    },
  ];
};
